import React from 'react';
import PropTypes from 'prop-types';
import * as types from '../../constants/actionTypes';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions as userActions } from '../../actions/userActions';
import { actions as accountActions } from '../../actions/accountActions';
import { actions as subscriptionActions } from '../../actions/subscriptionActions';
import { DEFAULT_PAGE_SIZE, MOBILE_WINDOW_BREAKPOINT } from '../../constants/constants';
import { CUSTOMER_ROLES } from '../../constants/roles';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import { Menu } from 'primereact/menu';
import UserProfileDrawer from '../User/UserProfileDrawer';
import UserLocationsDrawer from '../User/UserLocationsDrawer';
import UserNotificationsDrawer from '../User/UserNotificationsDrawer';
import Icon from '../Common/Icon';
import Notification from '../../components/Common/Notification';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import { convertFromRaw } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import './SubscriptionUsers.scss';
import UserBulkLocationSearchDrawer from '../User/UserBulkLocationSearchDrawer';
import Moment from "moment";
import { getNullableOnChangeValue } from '../../helpers/multiselect';

const COGMENUOPTIONS = {
    ViewProfile: "viewProfile",
    ViewLocationAccess: "viewLocationAccess",
    ViewEmailSettings: "emailNotifications",
    SendForgotPassword: "forgotPassword",
    ResendInvitation: "resendInvitation"
};

const ACTION_MENU_OPTIONS = {
    ViewProfile: 'viewProfile',
    LocationAccess: 'locationAccess',
    EmailSettings: 'emailNotificationSettings',
    PasswordReset: 'emailPasswordReset',
    MakeActive: 'changeActive',
    DeleteUser: 'deleteUser',
    ResendInvitation: 'resendInvitation'
};

const DRAWERS = {
    UserProfile: 'userProfile',
    LocationAccess: 'locationAccess',
    BulkLocationSearch: 'bulkLocationSearch',
    EmailSettings: 'emailSettings'
}

export class GlobalUsersPage extends React.Component {

    constructor(props, context) {
        super(props, context);

        const newUser = {
            id: 0,
            subscriptionId: 0,
            firstName: "",
            lastName: "",
            userName: "",
            email: "",
            workPhone: "",
            roleId: "",
            countryId: 0,
            managementCompanyId: 0,
            communityIds: "",
            departmentIds: "",
            excludeAsProjectCoach: false,
            allowTaskListExportToExcel: false,
            allowLocationTaskDateChanges: false,
            ssoId: "",
            lastLoginDate: "",
            isActive: true,
            selectedUserLocationAccess: '',
            userLocationRules: {
                areas: '',
                brands: '',
                continents: '',
                countries: '',
                locationTypes: '',
                managementCompanies: '',
                regions: ''
            }
        };

        this.state = {
            users: [],
            totalCount: 0,
            selectedUser: newUser,
            editUser: newUser,
            newUser: newUser,
            isFetching: false,
            isSaving: false,
            searchParams: {
                subscriptionId: null, // no subId
                isActive: true,
                firstName: "",
                lastName: "",
                userName: "",
                email: "",
                roleId: null,
                ssoId: "",
                pageSize: DEFAULT_PAGE_SIZE,
                pageNumber: 1,
                sortBy: "firstName",
                sortDir: "asc",
                returnAllResults: false
            },
            // Location Access and Bulk Location Search can be open at the same time
            // So we have to track each drawer separately, but we control the logic
            // inside of this.openDrawer() and this.closeDrawer() so that these
            // variables remain mutually exclusive when required to
            openDrawers: {
                userProfile: false,
                locationAccess: false,
                bulkLocationSearch: false,
                emailSettings: false
            },
            addBulkLocationsFunc: () => { /*Do nothing*/ },
            assignedLocations: [],
            showLocationAccessDrawerAfterSearch: false,
            selectUserAfterSearch: null
        };
        this.search.bind(this);

    }

    openDrawer = (drawerKey) => {
        let openDrawers = {
            userProfile: false,
            locationAccess: false,
            bulkLocationSearch: false,
            emailSettings: false
        };

        switch (drawerKey) {
            case DRAWERS.UserProfile:
                openDrawers.userProfile = true;
                break;
            case DRAWERS.LocationAccess:
                openDrawers.locationAccess = true;
                break;
            case DRAWERS.BulkLocationSearch:
                // Bulk Location Search is launched from Location Access, so we
                // leave both of them open
                openDrawers.locationAccess = true;
                openDrawers.bulkLocationSearch = true;
                break;
            case DRAWERS.EmailSettings:
                openDrawers.emailSettings = true;
                break;
            default:
                break;
        }

        this.setState({
            openDrawers: {
                ...this.state.openDrawers,
                ...openDrawers
            },
            isFormValid: true
        });
    }

    closeDrawer = (drawerKey) => {
        let openDrawers = {
            userProfile: false,
            locationAccess: false,
            bulkLocationSearch: false,
            emailSettings: false
        };

        switch (drawerKey) {
            case DRAWERS.BulkLocationSearch:
                // Bulk Location Search is launched from Location Access, so we
                // only close Bulk Location Search, but keep locationAccess open
                openDrawers.locationAccess = true;
                openDrawers.bulkLocationSearch = false;
                break;
            case DRAWERS.EmailSettings:
                openDrawers.emailSettings = false;
                break;
            default:
                break;
        }

        this.setState({
            openDrawers: {
                ...this.state.openDrawers,
                ...openDrawers
            }
        });
    }

    onEditUserComplete = () => {
        this.closeDrawer(DRAWERS.UserProfile);
        this.search();
    }

    onEditUserCancel = () => {
        this.closeDrawer(DRAWERS.UserProfile);
    }

    onEditLocationsComplete = () => {
        this.closeDrawer(DRAWERS.LocationAccess);
    }

    onBulkLocationSearchLaunch = (addBulkLocationsFunc, assignedLocations) => {
        this.setState({ addBulkLocationsFunc: addBulkLocationsFunc, assignedLocations: assignedLocations });
        this.openDrawer(DRAWERS.BulkLocationSearch);
    }

    onBulkLocationSearchComplete = (selectedLocations) => {
        this.closeDrawer(DRAWERS.BulkLocationSearch);
        this.state.addBulkLocationsFunc(selectedLocations);
    }

    onBulkLocationSearchCancel = () => {
        this.closeDrawer(DRAWERS.BulkLocationSearch);
    }

    onEmailSettingsComplete = () => {
        this.closeDrawer(DRAWERS.EmailSettings);
    }

    onEmailSettingsCancel = () => {
        this.closeDrawer(DRAWERS.EmailSettings);
    }

    async search() {
        try {
            this.setState({ isFetching: true });
            const result = await this.props.userActions.searchGlocalUsers(this.state.searchParams);
            const payload = result.payload;
            if (result.type === types.SEARCH_USER_SUCCESS) {

                let selectedUser = null;

                // After a user is invited and no location access was assigned to them
                // we need to select that user and open the location access drawer
                if(this.state.showLocationAccessDrawerAfterSearch && this.state.selectUserAfterSearch){
                    selectedUser = payload.results.find(m => m.id == this.state.selectUserAfterSearch);
                }

                this.setState({ 
                    users: payload.results, 
                    totalCount: payload.totalCount,
                    selectedUser: selectedUser,
                    editUser: selectedUser
                },() => {
                    if(this.state.selectUserAfterSearch && selectedUser){
                        this.openDrawer(DRAWERS.LocationAccess)
                    }                    
                });



            } else if (result.type === types.SEARCH_USER_FAILURE) {
                Notification("error", "An Error Occurred", payload.message)
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error);
        } finally {
            this.setState({ isFetching: false });
        }
    }

    filterChange = (e) => {
        let name = e.target.name;
        this.setState({ searchParams: { ...this.state.searchParams, [name]: e.target.value } });
    }

    onSearchBlur = () => {
        this.search();
    }

    keyUpFilter = (e) => {
        if (e.keyCode == 13) {
            this.search();
        }
    }

    selectUserRow = (e) => {
        const isEllipsisClick = e.originalEvent.target.className.indexOf('ellipsis-h') >= 0 || e.originalEvent.target.className.indexOf('ellipsis') >= 0;
        if (isEllipsisClick === true) {
            return;
        }
        
        // fires when user clicks a row in the datatable, but only on mobile do we show the menu
        // non mobile they need to click the actions button which calls onActionsClick
        if (this.state.windowWidth < MOBILE_WINDOW_BREAKPOINT) {
            this.setSelectedUser(e.value);
            this.menu.toggle(e.originalEvent);
        } else {
            this.onNameClick(e.value);
        }
    }

    onActionsClick = (rowData, e) => {
        this.setSelectedUser(rowData);
        this.menu.toggle(e);
    }

    setSelectedUser = (user) => {
        const newUser = { ...this.state.newUser, ...user };
        this.setState({ selectedUser: newUser, editUser: newUser });
    }

    onActiveFilterChange = (e) => {
        const selectedValue = getNullableOnChangeValue(e);
    
        this.setState(prevState => ({ searchParams: { ...prevState.searchParams, isActive: selectedValue} }), () => {
            this.search();
        });
    }

    onRoleFilterChange = (e) => {
        this.setState({ searchParams: { ...this.state.searchParams, roleId: e.value } }, () => {
            this.search();
        });
    }
    onNameClick = (rowData) => {
        this.setSelectedUser(rowData);
        this.openDrawer(DRAWERS.UserProfile);
    }

    activeTemplate = (rowData) => {
        /**
         * 1509: This cannot be a div, because the block display of a div pushes the wrapping
         * .ui-cell-data span up, triggering a y-axis overflow of the table cell, which causes
         * a scrollbar to show always.  Spans are inline-block by default, and we then center
         * the icons in the box via the .center-inline-block class, which centers via 33% left
         * and right margins.
         */
        if (rowData.isActive) {
            return <span className="text-success center-inline-block"><Icon name="check-circle" /></span>;
        } else {
            return <span className="text-danger center-inline-block"><Icon name="times-circle" /></span>;
        }
    }

    actionTemplate = (rowData) => {
        return <div className="text-center ellipsis" onClick={(e) => this.onActionsClick(rowData, e)}><Icon name="ellipsis-h" /></div>
    }

    dateTimeTemplate = (rowData) => {
        return (rowData.lastLogin) ? Moment(rowData.lastLogin).format("MM-DD-YYYY, h:mm A") : "N/A";
    }

    subscriptionNameTemplate = (rowData) => {
        return <span>{rowData.subscriptionName}</span>
    }

    firstNameTemplate = (rowData) => {
        return <button className="btn btn-link" onClick={() => this.onNameClick(rowData)}>{rowData.firstName}</button>
    }

    lastNameTemplate = (rowData) => {
        return <button className="btn btn-link" onClick={() => this.onNameClick(rowData)}>{rowData.lastName}</button>
    }

    cogMenuClick = (e) => {
        switch (e.item.value) {
            case COGMENUOPTIONS.ViewProfile:
                this.openDrawer(DRAWERS.UserProfile);
                break;
            case COGMENUOPTIONS.ViewLocationAccess:
                this.openDrawer(DRAWERS.LocationAccess);
                break;
            case COGMENUOPTIONS.ViewEmailSettings:
                this.openDrawer(DRAWERS.EmailSettings);
                break;
            case COGMENUOPTIONS.ResendInvitation:
                this.resendInvite();
                break;
            case COGMENUOPTIONS.SendForgotPassword:
                this.sendForgotPasswordEmail();
                break;
            default:
                break;
        }
    }

    sendForgotPasswordEmail = async () => {
        try {
            this.setState({ isSaving: true });
            const result = await this.props.accountActions.forgotPassword(this.state.editUser.userName);
            const payload = result.payload;
            if (result.type === types.FORGOT_PASSWORD_SUCCESS) {
                Notification("success", "Success", "Forgot Password Email Sent Successfully");
            } else {
                Notification("error", "An Error Occurred", payload.message)
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error)
        } finally {
            this.setState({ isSaving: false });
        }
    }

    resendInvite = async () => {
        try {
            this.setState({ isSaving: true });
            const result = await this.props.subscriptionActions.getInviteMessage(this.props.subscriptionId);
            const payload = result.payload;
            if (result.type === types.GET_SUBSCRIPTION_INVITEMESSAGE_SUCCESS) {
                const jsonContent = JSON.parse(payload.inviteMessage);
                const contentState = convertFromRaw(jsonContent);
                const inviteMessageHTML = stateToHTML(contentState);
                const inviteResponse = await this.props.userActions.reinviteUser(this.state.editUser.id, inviteMessageHTML);
                if (inviteResponse.type === types.INVITE_USER_SUCCESS) {
                    Notification("success", "Success", "Invitation Sent Successfully");
                } else {
                    Notification("error", "An Error Occurred sending invitation", payload.message)
                }
            } else if (result.type === types.GET_SUBSCRIPTION_INVITEMESSAGE_FAILURE) {
                Notification("error", "An Error Occurred getting invite message", payload.message)
            }
        } catch (error) {
            Notification("error", "An Error Occurred", error)
        } finally {
            this.setState({ isSaving: false });
        }
    }

    menuOptionClick = (e) => {
        switch (e.item.value) {
            case ACTION_MENU_OPTIONS.ViewProfile:
                this.openDrawer(DRAWERS.UserProfile);
                break;
            case ACTION_MENU_OPTIONS.LocationAccess:
                this.openDrawer(DRAWERS.LocationAccess);
                break;
            case ACTION_MENU_OPTIONS.PasswordReset:
                this.sendForgotPasswordEmail();
                break;
            case ACTION_MENU_OPTIONS.ResendInvitation:
                this.resendInvite();
                break;
            case ACTION_MENU_OPTIONS.EmailSettings:
                this.openDrawer(DRAWERS.EmailSettings);
                break;
            default:
                break;
        }
    }

    onCogSettingsClick = (e) => {
        this.cogmenu.toggle(e);
    }

    buildCogMenuOptions = () => {
        let cogMenuOptions = [
            { label: "Email Password Reset", value: COGMENUOPTIONS.SendForgotPassword, command: this.cogMenuClick }
        ];

        if (!this.state.openDrawers.userProfile) {
            cogMenuOptions.unshift({ label: "View Profile", value: COGMENUOPTIONS.ViewProfile, command: this.cogMenuClick })
        }

        if (!this.state.openDrawers.locationAccess) {
            cogMenuOptions.unshift({ label: "View Location Access", value: COGMENUOPTIONS.ViewLocationAccess, command: this.cogMenuClick })
        }

        if (!this.state.openDrawers.emailSettings) {
            cogMenuOptions.unshift({ label: "View Email Notification Settings", value: COGMENUOPTIONS.ViewEmailSettings, command: this.cogMenuClick })
        }

        if (this.state.editUser && this.state.editUser.hasAcceptedInvite === false) {
            cogMenuOptions.push({ label: "Resend Invitation", value: COGMENUOPTIONS.ResendInvitation, command: this.cogMenuClick });
        }

        return cogMenuOptions;
    }

    buildActionsMenuOptions = () => {
        const menuOptions = [
            { label: "View Profile", value: ACTION_MENU_OPTIONS.ViewProfile, command: this.menuOptionClick },
            { label: "Location Access", value: ACTION_MENU_OPTIONS.LocationAccess, command: this.menuOptionClick },
            { label: "Email Notification Settings", value: ACTION_MENU_OPTIONS.EmailSettings, command: this.menuOptionClick },
            { label: "Email Password Reset", value: ACTION_MENU_OPTIONS.PasswordReset, command: this.menuOptionClick }
        ];

        if (this.state.editUser && this.state.editUser.hasAcceptedInvite === false) {
            menuOptions.push({ label: "Resend Invitation", value: ACTION_MENU_OPTIONS.ResendInvitation, command: this.menuOptionClick });
        }

        return menuOptions;
    }

    onPageChange = (e) => {
        this.setState({ searchParams: { ...this.state.searchParams, pageNumber: (e.page + 1) } }, () => {
            this.search();
        });
    }

    render() {
        const displayColumns = (this.props.windowWidth < 500) ? 'none' : '';
        const mobileColumnWidth = (displayColumns) ? '25%' : ''; // display the few columns on mobile equally
        const activeOptions = [
            { label: "Yes", value: true },
            { label: "No", value: false },
            { label: "All", value: null }
        ];
        const roleOptions = [
            { label: "All", value: null },
            { label: CUSTOMER_ROLES.User.name, value: CUSTOMER_ROLES.User.id },
            { label: CUSTOMER_ROLES.Moderator.name, value: CUSTOMER_ROLES.Moderator.id },
            { label: CUSTOMER_ROLES.Admin.name, value: CUSTOMER_ROLES.Admin.id },
            { label: CUSTOMER_ROLES.Owner.name, value: CUSTOMER_ROLES.Owner.id }
        ];
        const menuOptions = this.buildActionsMenuOptions();
        
        const activeFilter = (
                    <Dropdown
                        name="isActive"
                        className="ui-column-filter w-100"
                        value={this.state.searchParams.isActive}
                        options={activeOptions}
                        optionLabel="label"
                        optionValue="value"
                        onChange={this.onActiveFilterChange}
                        placeholder="Select an option"
                    />
                );

        const roleidFilter = <Dropdown appendTo={document.body} name="roleId" className="ui-column-filter w-100" value={this.state.searchParams.roleId} options={roleOptions} onChange={this.onRoleFilterChange} />

        const firstNameFilter =
            <input type="text" name="firstName" placeholder="First Name" className="p-inputtext" onBlur={this.onSearchBlur} onChange={this.filterChange} onKeyUp={this.keyUpFilter} value={this.state.searchParams.firstName} />
        ;
        const lastNameFilter = 
            <input type="text" name="lastName" placeholder="Last Name" className="p-inputtext" onBlur={this.onSearchBlur} onChange={this.filterChange} onKeyUp={this.keyUpFilter} value={this.state.searchParams.lastName} />
        ;
        const userNameFilter = 
            <input type="text" name="userName" placeholder="Username" className="p-inputtext" onBlur={this.onSearchBlur} onChange={this.filterChange} onKeyUp={this.keyUpFilter} value={this.state.searchParams.userName} />
        ;
        const emailFilter = 
            <input type="text" name="email" placeholder="Email" className="p-inputtext" onBlur={this.onSearchBlur} onChange={this.filterChange} onKeyUp={this.keyUpFilter} value={this.state.searchParams.email} />
        ;
        const ssoidFilter = 
            <input type="text" name="ssoId" placeholder="SSO ID" className="p-inputtext" onBlur={this.onSearchBlur} onChange={this.filterChange} onKeyUp={this.keyUpFilter} value={this.state.searchParams.ssoId} />
        ;

        const cogMenuOptions = this.buildCogMenuOptions();

        const cogMenuButtons = [
            <button key="settings" name="settings" type="button" className="btn btn-custom" onClick={this.onCogSettingsClick} alt="config"><Icon name="cog" /></button>
        ];

        return (
            <div>
                <Menu model={menuOptions} popup={true} ref={el => this.menu = el} autoZIndex={false} baseZIndex={20001} />
                <Menu model={cogMenuOptions} popup={true} ref={el => this.cogmenu = el} autoZIndex={false} baseZIndex={20001} />
                <LoadingIndicator isLoading={this.state.isFetching || this.state.isSaving} hideWhileLoading={true}>
                    <div className="subscription-users-page">
                        <div className="page-header">
                            <h4>Users</h4>
                        </div>
                        <div className="subusers-results-table">
                            <DataTable
                                filterDisplay="row"
                                className="p-datatable-sm p-datatable-striped"
                                columnResizeMode="fit"
                                value={this.state.users}
                                selectionMode="single"
                                selection={this.state.selectedUser}
                                onSelectionChange={this.selectUserRow} >
                                <Column field="subscription" header="Subscription" body={this.subscriptionNameTemplate} filter={false} style={{ width: mobileColumnWidth }} />
                                <Column field="firstName" header="First Name" body={this.firstNameTemplate} filter={true} filterElement={firstNameFilter} style={{ width: mobileColumnWidth }} />
                                <Column field="lastName" header="Last Name" body={this.lastNameTemplate} filter={true} filterElement={lastNameFilter} style={{ width: mobileColumnWidth }} />
                                <Column field="userName" header="Username" filter={true} filterElement={userNameFilter} style={{ textOverflow: 'ellipsis', display: displayColumns }} />
                                <Column field="email" header="Email" filter={true} filterElement={emailFilter} style={{ textOverflow: 'ellipsis', display: displayColumns }} />
                                <Column field="lastLogin" header="Last Login" body={this.dateTimeTemplate} style={{ display: displayColumns }} />
                                <Column field="roleName" header="User Role" filter={true} filterElement={roleidFilter} style={{ display: displayColumns }} />
                                <Column field="ssoId" header="SSO Id" filter={true} filterElement={ssoidFilter} style={{ display: displayColumns }} />
                                <Column field="isActive" header="Active" body={this.activeTemplate} style={{ width: '120px' }} filter={true} filterElement={activeFilter} />
                                <Column field="dotdotdot" header="Actions" body={this.actionTemplate} style={{ width: '6em'}} />
                            </DataTable>
                            {
                                this.state.totalCount > this.state.searchParams.pageSize &&
                                <Paginator
                                    rows={this.state.searchParams.pageSize}
                                    totalRecords={this.state.totalCount}
                                    first={(this.state.searchParams.pageNumber - 1) * this.state.searchParams.pageSize}
                                    onPageChange={this.onPageChange} />
                            }
                            <div className="results-count">Total records: {this.state.totalCount}</div>
                        </div>
                    </div>
                </LoadingIndicator>

                {
                    this.state.openDrawers.userProfile ?
                        <UserProfileDrawer
                            subscriptionId={this.state.editUser.subscriptionId.toString()}
                            userId={this.state.editUser.id.toString()}
                            onComplete={this.onEditUserComplete}
                            onCancel={this.onEditUserCancel}
                            menuButtons={cogMenuButtons} />
                        :
                        null
                }
                {
                    this.state.openDrawers.locationAccess ?
                        <UserLocationsDrawer
                            subscriptionId={this.state.editUser.subscriptionId.toString()}
                            onComplete={this.onEditLocationsComplete}
                            user={this.state.editUser}
                            menuButtons={cogMenuButtons}
                            onBulkLocationSearchLaunch={this.onBulkLocationSearchLaunch} />
                        :
                        null
                }
                {
                    this.state.openDrawers.bulkLocationSearch ?
                        <UserBulkLocationSearchDrawer
                            subscriptionId={this.state.editUser.subscriptionId.toString()}
                            onComplete={this.onBulkLocationSearchComplete}
                            onCancel={this.onBulkLocationSearchCancel}
                            userFullName={this.state.editUser.firstName + ' ' + this.state.editUser.lastName}
                            userId={this.state.editUser.id.toString()}
                            menuButtons={cogMenuButtons}
                            selectedLocations={this.state.assignedLocations} />
                        :
                        null
                }
                {
                    this.state.openDrawers.emailSettings ?
                        <UserNotificationsDrawer
                            subscriptionId={this.state.editUser.subscriptionId.toString()}
                            userId={this.state.editUser.id}
                            userRole={this.state.editUser.roleName}
                            userFullName={this.state.editUser.firstName + ' ' + this.state.editUser.lastName}
                            onComplete={this.onEmailSettingsComplete}
                            onCancel={this.onEmailSettingsCancel}
                            menuButtons={cogMenuButtons} />
                        :
                        null
                }
            </div>
        );
    }

}
GlobalUsersPage.propTypes = {
    userActions: PropTypes.object.isRequired,
    accountActions: PropTypes.object.isRequired,
    subscriptionActions: PropTypes.object.isRequired,
    subscriptionId: PropTypes.string,
    windowWidth: PropTypes.number.isRequired,
    isSubPage: PropTypes.bool,
    onClose: PropTypes.func
};

function mapStateToProps(state, ownProps) {
    let subscriptionId = null;
    if (ownProps.subscriptionId && ownProps.isSubPage) {
        subscriptionId = ownProps.subscriptionId.toString();
    } else if (ownProps.match.params.subscriptionId) {
        subscriptionId = ownProps.match.params.subscriptionId;
    }

    return {
        subscriptionId: subscriptionId,
        windowWidth: state.window.width
    };
}

function mapDispatchToProps(dispatch) {
    return {
        userActions: bindActionCreators(userActions, dispatch),
        accountActions: bindActionCreators(accountActions, dispatch),
        subscriptionActions: bindActionCreators(subscriptionActions, dispatch)
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(GlobalUsersPage);

